import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Target, DollarSign, Activity, Crown, AlertCircle, BookOpen, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';


const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function Analytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [researchPapers, setResearchPapers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Fetch current user to ensure we have latest subscription status
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.subscription_status && userData.subscription_status !== 'free') {
          fetchAnalytics();
          fetchResearchPapers();
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setLoading(false);
    }
  };

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/analytics/overview`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch analytics');

      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResearchPapers = async () => {
    try {
      const response = await fetch(`${API}/analytics/research-papers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setResearchPapers(data);
      }
    } catch (error) {
      console.error('Research papers error:', error);
    }
  };

  if (!isPremium) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="analytics-page">
        <h1 className="text-4xl font-bold mb-8">Usage Analytics</h1>
        
        <Card className="p-12 glass-card text-center">
          <Crown className="h-16 w-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Premium Feature</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Unlock comprehensive usage analytics to optimize your research protocols. Track adherence, 
            dose consistency, cost efficiency, and protocol effectiveness over time.
          </p>
          <Button onClick={() => navigate('/subscription')} size="lg">
            Upgrade to Premium
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics || analytics.total_doses === 0) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="analytics-page">
        <h1 className="text-4xl font-bold mb-8">Usage Analytics</h1>
        
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No data yet. Start logging doses to see your analytics!
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const adherenceRate = analytics.adherence_rate.rate;
  const adherenceColor = adherenceRate >= 90 ? 'text-success' : adherenceRate >= 75 ? 'text-warning' : 'text-destructive';

  // Prepare consistency data for chart
  const consistencyChartData = Object.entries(analytics.dose_consistency).map(([peptide, data]) => ({
    peptide: peptide.length > 15 ? peptide.substring(0, 15) + '...' : peptide,
    score: data.consistency_score,
    doses: data.total_doses
  }));

  // Prepare cost data for pie chart
  const costChartData = Object.entries(analytics.cost_optimization.per_peptide || {}).map(([peptide, data]) => ({
    name: peptide.length > 15 ? peptide.substring(0, 15) + '...' : peptide,
    value: data.cost_per_dose
  }));

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="analytics-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
          Usage Analytics
        </h1>
        <p className="text-muted-foreground">
          Data-driven insights for your research protocols
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-4">
            <Target className="h-8 w-8 text-primary" />
          </div>
          <h3 className={`text-3xl font-bold ${adherenceColor}`}>
            {adherenceRate}%
          </h3>
          <p className="text-sm text-muted-foreground">Adherence Rate</p>
          <p className="text-xs text-muted-foreground mt-2">
            {analytics.adherence_rate.actual_doses}/{analytics.adherence_rate.expected_doses} doses
          </p>
        </Card>

        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-4">
            <Activity className="h-8 w-8 text-success" />
          </div>
          <h3 className="text-3xl font-bold">
            {analytics.total_doses}
          </h3>
          <p className="text-sm text-muted-foreground">Total Doses</p>
          <p className="text-xs text-success mt-2">
            {Object.keys(analytics.dose_consistency).length} peptides tracked
          </p>
        </Card>

        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="h-8 w-8 text-warning" />
          </div>
          <h3 className="text-3xl font-bold">
            £{analytics.cost_optimization.average_cost_per_dose.toFixed(2)}
          </h3>
          <p className="text-sm text-muted-foreground">Avg Cost/Dose</p>
          <p className="text-xs text-muted-foreground mt-2">
            £{analytics.cost_optimization.total_cost.toFixed(2)} total
          </p>
        </Card>

        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="h-8 w-8 text-info" />
          </div>
          <h3 className="text-3xl font-bold">
            {analytics.trends.length}
          </h3>
          <p className="text-sm text-muted-foreground">Weeks Tracked</p>
          <p className="text-xs text-muted-foreground mt-2">
            {analytics.trends.length > 0 ? analytics.trends[analytics.trends.length - 1].week : 'N/A'}
          </p>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Dose Consistency Chart */}
        <Card className="p-6 glass-card">
          <h3 className="text-xl font-bold mb-6">Dose Consistency Score</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={consistencyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="peptide" stroke="#A1A1AA" fontSize={12} />
              <YAxis stroke="#A1A1AA" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181B',
                  border: '1px solid #3B82F6',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="score" fill="#3B82F6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-4">
            Higher scores indicate more consistent dosing (100 = perfect consistency)
          </p>
        </Card>

        {/* Weekly Trends */}
        <Card className="p-6 glass-card">
          <h3 className="text-xl font-bold mb-6">Weekly Dose Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="week" stroke="#A1A1AA" fontSize={12} />
              <YAxis stroke="#A1A1AA" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181B',
                  border: '1px solid #3B82F6',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="doses" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-4">
            Track your dosing frequency over the last 12 weeks
          </p>
        </Card>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dose Consistency Details */}
        <Card className="p-6 glass-card">
          <h3 className="text-xl font-bold mb-4">Consistency Details</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {Object.entries(analytics.dose_consistency).map(([peptide, data]) => (
              <div key={peptide} className="p-4 rounded-lg bg-secondary/50 border border-border">
                <h4 className="font-bold mb-2">{peptide}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Avg Dose</p>
                    <p className="font-mono">{data.average_dose_mcg} mcg</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Consistency</p>
                    <p className="font-bold text-primary">{data.consistency_score}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Range</p>
                    <p className="font-mono text-xs">{data.min_dose}-{data.max_dose} mcg</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Doses</p>
                    <p>{data.total_doses}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Protocol Effectiveness */}
        <Card className="p-6 glass-card">
          <h3 className="text-xl font-bold mb-4">Protocol Effectiveness</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {Object.entries(analytics.protocol_effectiveness).map(([peptide, data]) => (
              <div key={peptide} className="p-4 rounded-lg bg-secondary/50 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold">{peptide}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    data.status === 'excellent' ? 'bg-success/20 text-success' :
                    data.status === 'good' ? 'bg-info/20 text-info' :
                    'bg-warning/20 text-warning'
                  }`}>
                    {data.status.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Effectiveness</p>
                    <p className="font-bold text-success">{data.effectiveness_score}/100</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Duration</p>
                    <p>{data.duration_days} days</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Avg Side Effects</p>
                    <p>{data.average_side_effects}/5</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Doses</p>
                    <p>{data.total_doses}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Insights Section */}
      <Card className="mt-6 p-6 glass-card">
        <h3 className="text-xl font-bold mb-4">💡 Key Insights</h3>
        <div className="space-y-3">
          {adherenceRate >= 90 && (
            <div className="p-3 rounded-lg bg-success/10 border border-success/30">
              <p className="text-success">
                ✨ Excellent adherence! You're consistently following your protocol schedule.
              </p>
            </div>
          )}
          {adherenceRate < 75 && (
            <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
              <p className="text-warning">
                ⚠️ Consider setting up more reminders to improve adherence. Missed {analytics.adherence_rate.missed_doses} doses.
              </p>
            </div>
          )}
          {Object.values(analytics.dose_consistency).some(d => d.consistency_score < 70) && (
            <div className="p-3 rounded-lg bg-info/10 border border-info/30">
              <p className="text-info">
                📊 Some peptides show high dose variation. Review your calculator settings for more precise measurements.
              </p>
            </div>
          )}
          {analytics.cost_optimization.total_cost > 0 && (
            <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
              <p className="text-primary">
                💰 Total research investment: £{analytics.cost_optimization.total_cost.toFixed(2)}. 
                Average cost per dose: £{analytics.cost_optimization.average_cost_per_dose.toFixed(2)}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Research Papers Linked to Your Peptides */}
      {researchPapers && researchPapers.peptides && researchPapers.peptides.length > 0 && (
        <Card className="mt-6 p-6 glass-card" data-testid="research-papers-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h3 className="text-xl font-bold">Research Papers for Your Peptides</h3>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary" data-testid="research-papers-count">
              {researchPapers.total_papers} papers
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Scientific literature linked to the peptides you're actively tracking.
          </p>
          <div className="space-y-4 max-h-[480px] overflow-y-auto">
            {researchPapers.peptides.map((p) => (
              <div
                key={p.peptide_id}
                className="p-4 rounded-lg bg-secondary/50 border border-border"
                data-testid={`research-peptide-${p.peptide_id}`}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div>
                    <h4 className="font-bold text-base">{p.peptide_name}</h4>
                    <p className="text-xs text-muted-foreground">{p.category} • {p.total_doses} doses logged</p>
                  </div>
                  <button
                    onClick={() => navigate(`/peptides/${p.peptide_id}`)}
                    className="text-xs text-primary hover:underline whitespace-nowrap"
                    data-testid={`view-peptide-${p.peptide_id}`}
                  >
                    View peptide →
                  </button>
                </div>
                {p.papers.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No PubMed references on file yet.</p>
                ) : (
                  <ul className="space-y-1.5 mt-2">
                    {p.papers.map((paper, idx) => (
                      <li key={idx}>
                        <a
                          href={paper.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-primary hover:underline group"
                          data-testid={`paper-link-${p.peptide_id}-${idx}`}
                        >
                          <ExternalLink className="h-3 w-3 opacity-60 group-hover:opacity-100" />
                          <span className="truncate">{paper.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
